/**
 * Canonical execution priority for Radar actions.
 *
 * RUBRIC POINTS AND EXECUTION PRIORITY ARE DIFFERENT QUESTIONS and this
 * module answers only the second one:
 *
 *   rubric points  - how much fixing this moves the Radar score
 *                    (projection.ts, unchanged by this module)
 *   priority score - which fix the customer should do FIRST
 *
 * Conflating them is tempting because projection.ts already computes a
 * measured per-action number. It is deliberately not reused here: a fix
 * worth many rubric points can still be the wrong thing to do first, and
 * "improves your score" is not the same claim as "earns you traffic".
 *
 * WHY EVERY CURRENT ACTION IS UNRANKED. The formula needs traffic at
 * stake in clicks per period. Radar's thirteen tools measure AI visibility -
 * bot access, structured data, llms.txt, citations, share of voice - and
 * none of them measures clicks. There is therefore no defensible
 * trafficAtStake for any action generated today, so every one carries
 * priorityScore null and falls through to the deterministic fallback tier.
 *
 * That is the honest outcome, not a gap to paper over. Inventing a
 * per-category "estimated monthly clicks" table would make the ranking
 * look measured while being a guess, which is exactly the failure mode
 * the striking-distance check was parked for. The ranked tier activates
 * on its own the moment an action arrives carrying real inputs - see the
 * ordering test that proves this.
 */

import type { ActionItem, ActionPriority, ToolKey } from '@/types/radar'

/**
 * The minimum an action needs to be ordered. Kept structural so the
 * projection's RecoverableAction can share the comparator without being
 * converted into an ActionItem — one comparator, two view models.
 */
export interface PrioritizableAction {
  id: string
  effort: ActionItem['effort']
  impact: ActionItem['impact']
  priorityDetail?: ActionPriority
}

/**
 * Version of the ACTION ORDERING methodology. Bumped when the priority
 * contract or the canonical comparator changes.
 *
 * DELIBERATELY SEPARATE from two existing versions, and not a substitute
 * for either:
 *
 *   RADAR_METHOD_VERSION (trendstore.ts) - the COMPOSITE SCORE methodology.
 *     Untouched by T4. The score math, rubric attribution and projection
 *     ceilings are byte-identical, so historical scores stay comparable.
 *   V0_WEIGHT_SET_VERSION (calibration.ts) - the weight set. Untouched.
 *
 * v0 was the pre-T4 state: three independent orderings (impact+effort in
 * actions.ts, scorePoints+effort in projection.ts, effort+scorePoints in
 * PriorityRoadmap) with no priority contract. v1 is one canonical
 * comparator with an explicit measured/unranked/monitoring contract.
 */
export const RADAR_ACTION_METHOD_VERSION = 'actions-v2-finding-type'

/**
 * v2 (T5) adds finding provenance: every action now carries a findingType of
 * deterministic or judgement, derived from tier-map.ts.
 *
 * Bumped HERE rather than in a new constant: this string already means "the
 * action/finding methodology", and provenance is part of that contract. A
 * second constant would leave a consumer having to read two versions to know
 * what an action means. Still separate from RADAR_METHOD_VERSION (composite
 * score) and V0_WEIGHT_SET_VERSION, both untouched by T4 and T5.
 */

/**
 * Sources whose actions are monitoring or investigation rather than a fix
 * the customer can implement and verify.
 *
 * The distinction is "is there a change I can make to my own site, and
 * could a recovery be estimated for it" - not "is it important". Citation
 * rate, Reddit presence, answer-engine placement, share of voice and
 * source influence all describe how THIRD-PARTY systems represent you.
 * The action is to watch, investigate or do off-site work, and no honest
 * recovery estimate exists for any of them.
 *
 * hallucinationCheck is deliberately NOT here: its actions edit your own
 * llms.txt and schema ("Update your llms.txt with accurate facts. Add
 * complete Organization and Product schema"), which is a direct
 * implementation fix even though its recovery is unmeasured.
 */
const MONITORING_SOURCES: ReadonlySet<string> = new Set<ToolKey | string>([
  'citation',
  'reddit',
  'answer',
  'promptSov',
  'sourceInfluence',
])

export function isMonitoringSource(source: string): boolean {
  return MONITORING_SOURCES.has(source)
}

/**
 * Compute clicks per developer hour, or null when the inputs cannot
 * support the claim.
 *
 * UNITS: `trafficAtStake` is clicks per MONTH and `effortHours` is
 * developer hours, so the quotient is "monthly clicks recovered per
 * developer hour". Any display must say developer hour, never a bare
 * "per hour", which would read as a rate over elapsed time.
 *
 * Returns null rather than throwing or defaulting: an action with
 * unknown inputs is unranked, never bottom-ranked, because a computed
 * zero would be a measurement claim we cannot make.
 */
export function computePriorityScore(
  inputs: Pick<
    ActionPriority,
    'trafficAtStake' | 'recoveryRate' | 'confidence' | 'effortHours'
  >
): number | null {
  const { trafficAtStake, recoveryRate, confidence, effortHours } = inputs

  for (const value of [trafficAtStake, recoveryRate, confidence, effortHours]) {
    if (value === null || value === undefined) return null
    if (!Number.isFinite(value)) return null
  }

  // Non-null asserted: the loop above rejected null/undefined.
  const traffic = trafficAtStake as number
  const recovery = recoveryRate as number
  const conf = confidence as number
  const hours = effortHours as number

  // Effort divides, so zero or negative is not merely invalid input - it
  // would produce Infinity and float the action to the top of the list.
  if (hours <= 0) return null
  // Rates are proportions. Out-of-range values indicate a caller bug, and
  // silently clamping them would hide it while still emitting a number.
  if (recovery < 0 || recovery > 1) return null
  if (conf < 0 || conf > 1) return null
  if (traffic < 0) return null

  // Full precision retained. Round at the display edge only.
  return (traffic * recovery * conf) / hours
}

/** Build the priority block for an action that supplies no measured inputs. */
export function unrankedPriority(source: string): ActionPriority {
  return {
    trafficAtStake: null,
    recoveryRate: null,
    confidence: null,
    effortHours: null,
    priorityScore: null,
    prioritySource: isMonitoringSource(source) ? 'monitoring' : 'unranked',
  }
}

// ── Fallback ordering for unranked implementation actions ──────────────────
// The pre-existing composite, preserved deliberately. It is a heuristic over
// hand-authored strings, not a measurement, so it ranks WITHIN the unranked
// tier and never competes with a real priorityScore.
const IMPACT_SCORE = { high: 3, medium: 2, low: 1 } as const
const EFFORT_SCORE = { low: 3, medium: 2, high: 1 } as const

export function fallbackCompositeScore(action: PrioritizableAction): number {
  return IMPACT_SCORE[action.impact] + EFFORT_SCORE[action.effort]
}

/**
 * Tier: lower sorts first. Ranked > unranked implementation > monitoring.
 *
 * Finiteness, not just non-null: `priorityScore` is typed `number | null`
 * and NaN is a number, so a malformed persisted or imported action could
 * otherwise enter tier 0 and make the comparator return NaN — which would
 * silently destroy the total order rather than fail loudly.
 */
function tier(action: PrioritizableAction): number {
  if (Number.isFinite(action.priorityDetail?.priorityScore)) return 0
  return action.priorityDetail?.prioritySource === 'monitoring' ? 2 : 1
}

/** Comparable number, or 0 for anything non-finite. Keeps ties deterministic. */
function finite(value: number | null | undefined): number {
  return Number.isFinite(value) ? (value as number) : 0
}

/**
 * Ids are `action-<n>`, so plain lexicographic order puts `action-10` before
 * `action-2`. Numeric collation keeps the final tie-break agreeing with the
 * generation sequence, which is what the pre-T4 stable sort effectively did.
 *
 * The locale is PINNED rather than left as `undefined`. A comparator whose
 * output depends on the host locale is not canonical, and this module's
 * whole claim is a stable order that persisted and exported lists can rely
 * on. Same reasoning as the pinned number formatter in the
 * visibility-to-click statement.
 */
const ID_COLLATOR = new Intl.Collator('en', { numeric: true })

function compareIds(a: string, b: string): number {
  return ID_COLLATOR.compare(a, b)
}

/**
 * THE canonical comparator. Every customer-facing surface sorts through
 * this and nothing re-sorts afterwards, so the same action set produces
 * the same order in the panel, the roadmap, the export and the webhook.
 *
 * Total and deterministic: it ends on action id, so there is no reliance
 * on Array.prototype.sort stability or on insertion order.
 */
export function compareActions(
  a: PrioritizableAction,
  b: PrioritizableAction
): number {
  const tierDiff = tier(a) - tier(b)
  if (tierDiff !== 0) return tierDiff

  const aP = a.priorityDetail
  const bP = b.priorityDetail

  // Tier 0: measured. Highest clicks per developer hour first. Every value goes
  // through finite() so a malformed input can never yield a NaN comparison.
  if (tier(a) === 0 && tier(b) === 0) {
    const byScore = finite(bP?.priorityScore) - finite(aP?.priorityScore)
    if (byScore !== 0) return byScore
    // Tie-breakers, most-defensible signal first.
    const byConfidence = finite(bP?.confidence) - finite(aP?.confidence)
    if (byConfidence !== 0) return byConfidence
    const byTraffic = finite(bP?.trafficAtStake) - finite(aP?.trafficAtStake)
    if (byTraffic !== 0) return byTraffic
    const byEffort = finite(aP?.effortHours) - finite(bP?.effortHours)
    if (byEffort !== 0) return byEffort
    return compareIds(a.id, b.id)
  }

  // Tiers 1 and 2: the hand-authored composite, then id.
  const composite = fallbackCompositeScore(b) - fallbackCompositeScore(a)
  if (composite !== 0) return composite
  return compareIds(a.id, b.id)
}

/** Sort a copy. Never mutates the caller's array. */
export function sortActions<T extends PrioritizableAction>(actions: T[]): T[] {
  return [...actions].sort(compareActions)
}

/**
 * Split into the two groups the UI renders separately. Both halves keep
 * canonical order, so a consumer can render one list or two without the
 * order changing underneath it.
 */
export function partitionActions<T extends PrioritizableAction>(
  actions: T[]
): { fixes: T[]; monitoring: T[] } {
  const sorted = sortActions(actions)
  return {
    fixes: sorted.filter(
      a => a.priorityDetail?.prioritySource !== 'monitoring'
    ),
    monitoring: sorted.filter(
      a => a.priorityDetail?.prioritySource === 'monitoring'
    ),
  }
}
